import { Injectable } from '@angular/core';
import { CameraResultType, CameraSource } from '@capacitor/camera';
import { PhotoPickerResult, PhotoPickerStrategy } from '../photo-picker.types';
import { PlatformDetectorService } from '../platform-detector.service';
import { CameraService } from '../camera.service';
import { compressBase64ToBase64 } from '../../../app/shared/utils/image-compression.utils';

/**
 * Photo picker strategy for web platforms.
 * Uses CameraResultType.Base64 since Filesystem.readFile() is not available on web.
 */
@Injectable({
    providedIn: 'root'
})
export class WebPhotoPickerStrategy implements PhotoPickerStrategy {
    private static readonly USER_CANCEL_MESSAGES = [
        'No image picked',
        'User cancelled photos app'
    ];

    constructor(
        private readonly platformDetector: PlatformDetectorService,
        private readonly cameraService: CameraService
    ) {}

    isAvailable(): boolean {
        return this.platformDetector.isWebPlatform();
    }

    async pickPhoto(source: CameraSource): Promise<PhotoPickerResult | null> {
        try {
            // quality:75 is the sweet spot for JPEG (Capacitor honours it
            // on web too). width AND height bound both axes so portrait
            // photos picked from a desktop browser still get scaled.
            // Note: Capacitor's web fallback for Camera is best-effort
            // scaling — the result is passed through
            // browser-image-compression below as defense-in-depth.
            const image = await this.cameraService.getPhoto({
                quality: 75,
                allowEditing: false,
                resultType: CameraResultType.Base64,
                saveToGallery: false,
                source,
                width: 1920,
                height: 1920
            });

            if (!image.base64String) {
                throw new Error('No base64 data received from camera');
            }

            const sourceMimeType = image.format ? `image/${image.format}` : 'image/jpeg';
            const compressedBase64 = await compressBase64ToBase64(image.base64String, sourceMimeType);
            const fileName = this.generateFileName('jpeg');

            return {
                fileName,
                base64Data: compressedBase64
            };
        } catch (error) {
            if (this.isUserCancellation(error)) {
                return null;
            }
            throw error;
        }
    }

    private isUserCancellation(error: unknown): boolean {
        if (error instanceof Error) {
            return WebPhotoPickerStrategy.USER_CANCEL_MESSAGES.includes(error.message);
        }
        return false;
    }

    private generateFileName(format?: string): string {
        const timestamp = Date.now();
        const extension = format || 'jpeg';
        return `photo_${timestamp}.${extension}`;
    }
}
